<script setup lang="ts">
import { ChevronLeft, ChevronRight, ExternalLinkIcon, Menu, XIcon } from 'lucide-vue-next'
import { ref } from 'vue'
import { ISelectOption } from '@/types/interfaces/common.interface'
import RenderPage from './RenderPage.vue'
/**
 * define props
 */
const props = defineProps({
  docPath: {
    type: String,
    required: true,
  },
  docPageKeyStore: {
    type: String,
    default: '',
  },
})

const bufferCurrentPage = ref<number>(1)
const currentPage = ref<number>(Number(localStorage.getItem(props.docPageKeyStore)) || 1)
const showControlPage = ref<boolean>(false)
const scaleText = ref<string>('1')
const totalPage = ref<number>(1)
const isPageRendering = ref<boolean>(true)

const scaleOption = ref<ISelectOption[]>([
  { label: '25%', value: '0.25' },
  { label: '50%', value: '0.5' },
  { label: '75%', value: '0.75' },
  { label: '100%', value: '1' },
  { label: '125%', value: '1.25' },
  { label: '150%', value: '1.5' },
  { label: '175%', value: '1.75' },
  { label: '200%', value: '2' },
  { label: 'Fit Width', value: 'fit-width' },
  { label: 'Fit Height', value: 'fit-height' },
])

const prevPage = async () => {
  const page = currentPage.value > 1 ? currentPage.value - 1 : currentPage.value
  changePage(page)
}

const nextPage = async () => {
  const page = currentPage.value < totalPage.value ? currentPage.value + 1 : currentPage.value
  changePage(page)
}

const updateStoragePage = () => {
  if (props.docPageKeyStore) localStorage.setItem(props.docPageKeyStore, `${currentPage.value}`)
}

const changePage = (page: number) => {
  if (page <= totalPage.value && page >= 0) {
    currentPage.value = page
  }
  bufferCurrentPage.value = currentPage.value
  updateStoragePage()
}

const validateInputNumber = (event: any) => {
  // Replace all non-digit characters globally (e.g., /[^0-9]/g)
  // or to allow decimals use /[^0-9.]/g and add logic to prevent multiple dots.
  const changedValue = event.target?.value.replace(/\D/g, '')
  const page =
    Number(changedValue) <= totalPage.value && Number(changedValue >= 0)
      ? Number(changedValue)
      : currentPage.value || 1
  changePage(page)
}

const toggleShowPanel = () => {
  showControlPage.value = !showControlPage.value
}

const handelLoadSuccess = (value: { totalPage: number }) => {
  isPageRendering.value = false
  totalPage.value = value.totalPage
}

const onScrollPage = (page: number) => {
  bufferCurrentPage.value = currentPage.value = page
  updateStoragePage()
}
</script>

<template>
  <div :class="{ 'document-grids': !showControlPage, 'document-control-grids': showControlPage }">
    <div class="document-menu">
      <button class="mr-auto" @click="toggleShowPanel">
        <Menu v-show="!showControlPage" :size="20" />
        <XIcon v-show="showControlPage" :size="20" />
      </button>
      <div class="flex align-items-center">
        <button @click="prevPage">
          <ChevronLeft :size="20" />
        </button>
        <div class="input-wrapper">
          <input
            v-model.number="bufferCurrentPage"
            type="number"
            name="doc_page"
            class="input-text"
            :disabled="isPageRendering"
            @input="validateInputNumber"
          />
        </div>
        <div class="pr-2">/</div>
        <div class="pr-2">
          <p>{{ totalPage }}</p>
        </div>
        <button @click="nextPage">
          <ChevronRight :size="20" />
        </button>
      </div>
      <div class="flex justify-center items-center">
        <select v-model="scaleText" class="scale-option">
          <option v-for="opt in scaleOption" :key="`s-opt-${opt}`" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>
      <div class="ml-auto cursor-pointer">
        <a :href="docPath" target="_blank"><ExternalLinkIcon :size="20" /></a>
      </div>
    </div>
    <div v-show="showControlPage" class="page-control">
      <RenderPage
        v-if="showControlPage"
        :current-page="currentPage"
        :doc-path="docPath"
        scale-text="fit-width"
        scroll-style="smooth"
        highlight-active
        can-click-page
        class="p-4"
        @click-page="(page) => changePage(page)"
      >
        <template #after-page="{ page }">
          <div class="w-full flex justify-center">
            <p class="text-sm text-gray-300">
              {{ page }}
            </p>
          </div>
        </template>
      </RenderPage>
    </div>
    <div class="main-pdf">
      <RenderPage
        :current-page="currentPage"
        :doc-path="docPath"
        :scale-text="scaleText"
        class="p-5"
        detect-scroll
        @load-success="handelLoadSuccess"
        @change-page="onScrollPage"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.document-grids {
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 48px 1fr;
  grid-template-areas:
    'p-menu p-menu p-menu p-menu'
    'p-main p-main p-main p-main';
}
.document-control-grids {
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 48px 1fr;
  grid-template-areas:
    'p-menu p-menu p-menu p-menu'
    'p-control p-main p-main p-main';
}

.document-menu {
  grid-area: p-menu;
  padding: 0.5rem 1rem;
  background: $gs-2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 14px;
  color: white;
  @include primary-box-shadow;
}
.page-control {
  grid-area: p-control;
  background: $gs-1;
  width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-right: 1.5px solid $gs-2;
  overflow: hidden;
  .pdf-container {
    margin: auto;
    max-width: 15vw;
  }
}
.main-pdf {
  grid-area: p-main;
  background: $gs-1;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.input-wrapper {
  width: 54px;
  padding: 0 5px;
  color: $gs-1;
  .input-text {
    width: 100%;
    -webkit-appearance: none;
    -moz-appearance: textfield;
    margin: 0;
    background: $gs-3;
    text-align: end;
    color: white;
    border-radius: 6px;
  }
}
::v-deep(.page-view-wrapper) {
  canvas {
    width: 100% !important;
    height: auto !important;
  }
}
.pdf-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}
::v-deep(.page-overall-wrapper) {
  padding-bottom: 10px;
}

.page-control {
  ::v-deep(.pdf-container .page-overall-wrapper) {
    cursor: pointer;
    color: white;
    opacity: 0.5;
    canvas {
      width: 100% !important;
      height: auto !important;
      border-radius: 8px;
    }
    &.current {
      opacity: 1;
    }
  }
}
.scale-option {
  color: $gs-1;
  padding: 5px 7px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
